import { ChartBinSize } from '@frontend/chart/types';
import { DateStep } from '@frontend/common';
import { FromToDateInput, SelectInputV2 } from '@frontend/components/basic-forms';
import { IoTBrandSelect, IoTTypeSelect } from '@frontend/iot/select';
import { CommonMessage } from '@frontend/lang';
import { ProductSelectV2 } from '@frontend/product/select';
import { SpotSelectV2 } from '@frontend/spot/select';
import { UserSelectV2 } from '@frontend/user/select';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';

import { allowedDateStepsForBinSize, getBinSizeMaxWindow, rangeFromDateStep } from './insight-default-range';
import { useInsightDetailFilters } from './insight-detail-filters.context';
import { BIN_SIZE_MAX_RANGE_MS, isBinSizeAllowedForRange } from './insight-filter-guards';

const BIN_SIZE_MESSAGE: Record<ChartBinSize, { id: string; defaultMessage: string; description: string }> = {
    [ChartBinSize.HOUR]: {
        id: 'platform.analytics.insights.insight-detail-filter-bar.bin-size.hour',
        description: 'Label for the hour bin size option on insight detail filters',
        defaultMessage: 'Hour'
    },
    [ChartBinSize.DAY]: {
        id: 'platform.analytics.insights.insight-detail-filter-bar.bin-size.day',
        description: 'Label for the day bin size option on insight detail filters',
        defaultMessage: 'Day'
    },
    [ChartBinSize.MONTH]: {
        id: 'platform.analytics.insights.insight-detail-filter-bar.bin-size.month',
        description: 'Label for the month bin size option on insight detail filters',
        defaultMessage: 'Month'
    },
    [ChartBinSize.FULL_PERIOD]: {
        id: 'platform.analytics.insights.insight-detail-filter-bar.bin-size.full-period',
        description: 'Label for the full-period bin size option on insight detail filters',
        defaultMessage: 'Full period'
    }
};

const fieldClassName = 'mb-0 w-full min-w-[180px] sm:w-56';

const formatFullPeriodMaxLabel = (formatMessage: IntlShape['formatMessage'], binSize: ChartBinSize): string => {
    const maxWindow = getBinSizeMaxWindow(binSize);
    const maxWindowLabel = formatMessage(
        maxWindow.unit === 'years'
            ? {
                  id: 'platform.analytics.insights.insight-detail-filter-bar.max-window-years',
                  description: 'Year count used inside the Full period max window label on insight date range presets',
                  defaultMessage: '{count, plural, one {# year} other {# years}}'
              }
            : {
                  id: 'platform.analytics.insights.insight-detail-filter-bar.max-window-days',
                  description: 'Day count used inside the Full period max window label on insight date range presets',
                  defaultMessage: '{count, plural, one {# day} other {# days}}'
              },
        { count: maxWindow.count }
    );
    return formatMessage(
        {
            id: 'platform.analytics.insights.insight-detail-filter-bar.full-period-max',
            description: 'Full period date-range preset label showing the max allowed window for the current bin size',
            defaultMessage: 'Full period (max {maxWindow})'
        },
        { maxWindow: maxWindowLabel }
    );
};

export const InsightDetailFilterBar = () => {
    const filters = useInsightDetailFilters();
    const { formatMessage } = useIntl();

    if (!filters.active) {
        return null;
    }

    const showBinSelect = filters.showDateRange && !filters.lockBinSize && filters.editableBinSizes.length > 0;
    const binOptions = filters.editableBinSizes
        .filter((bin) => bin === filters.binSize || isBinSizeAllowedForRange(bin, filters.after, filters.before))
        .map((bin) => ({
            value: bin,
            label: formatMessage(BIN_SIZE_MESSAGE[bin])
        }));

    const showEntity = filters.entityFilters.length > 0;
    const fullPeriodLabel = formatFullPeriodMaxLabel(formatMessage, filters.binSize);

    return (
        <div className='flex w-full flex-col gap-2'>
            <div className='flex w-full flex-wrap items-end justify-end gap-3'>
                {filters.showDateRange ? (
                    <div className='min-w-64 flex-1 sm:max-w-md sm:flex-none'>
                        <div className='text-foreground-default mb-1 text-sm font-medium'>
                            <FormattedMessage
                                id='platform.analytics.insights.insight-detail-filter-bar.date-range'
                                description='Label above the date range control on insight detail filters'
                                defaultMessage='Date range'
                            />
                        </div>
                        <div className='h-11'>
                            <FromToDateInput
                                fromDate={filters.after}
                                toDate={filters.before}
                                period={DateStep.CUSTOM}
                                allowedDateSteps={allowedDateStepsForBinSize(filters.binSize)}
                                maxRangeMs={BIN_SIZE_MAX_RANGE_MS[filters.binSize]}
                                dateStepLabelOverrides={{
                                    [DateStep.FULL_PERIOD]: fullPeriodLabel
                                }}
                                onChange={(from, to) => {
                                    if (from && to) {
                                        filters.setRange(from, to);
                                    }
                                }}
                                onClick={(step) => {
                                    const next = rangeFromDateStep(step as DateStep, filters.binSize);
                                    filters.setRange(next.after, next.before);
                                }}
                            />
                        </div>
                    </div>
                ) : null}

                {showBinSelect ? (
                    <div className='w-full min-w-[160px] sm:w-48'>
                        <SelectInputV2
                            id='insight-detail-bin-size'
                            label={
                                <FormattedMessage
                                    id='platform.analytics.insights.insight-detail-filter-bar.bin-size'
                                    description='Label above the bin size select on insight detail filters'
                                    defaultMessage='Bin size'
                                />
                            }
                            hidePlaceholder
                            disableSearch
                            isClearable={false}
                            className='mb-0'
                            options={binOptions}
                            value={filters.binSize}
                            onSelect={(option) => {
                                if (!option) {
                                    return;
                                }
                                const next = option.value as ChartBinSize;
                                if (isBinSizeAllowedForRange(next, filters.after, filters.before)) {
                                    filters.setBinSize(next);
                                }
                            }}
                        />
                    </div>
                ) : null}

                {showEntity && filters.entityFilters.includes('spot') ? (
                    <SpotSelectV2
                        id='insight-detail-spot'
                        className={fieldClassName}
                        isClearable
                        value={filters.spotId}
                        onChange={filters.setSpotId}
                    />
                ) : null}

                {showEntity && filters.entityFilters.includes('product') ? (
                    <ProductSelectV2
                        id='insight-detail-product'
                        className={fieldClassName}
                        isClearable
                        value={filters.productId}
                        onChange={filters.setProductId}
                    />
                ) : null}

                {showEntity && filters.entityFilters.includes('user') ? (
                    <UserSelectV2
                        id='insight-detail-user'
                        className={fieldClassName}
                        isClearable
                        value={filters.userId}
                        onChange={filters.setUserId}
                    />
                ) : null}

                {showEntity && filters.entityFilters.includes('iotType') ? (
                    <IoTTypeSelect
                        id='insight-detail-iot-type'
                        className={fieldClassName}
                        isClearable
                        label={
                            <FormattedMessage
                                id='platform.analytics.insights.insight-detail-filter-bar.iot-type'
                                description='Label above the IoT type select on insight detail filters'
                                defaultMessage='IoT type'
                            />
                        }
                        value={filters.iotType}
                        onSelect={(option) => filters.setIotType(option?.value ?? null)}
                    />
                ) : null}

                {showEntity && filters.entityFilters.includes('iotBrand') ? (
                    <IoTBrandSelect
                        id='insight-detail-iot-brand'
                        className={fieldClassName}
                        isClearable
                        label={CommonMessage.OBJECTS.COMMON_FIELDS.BRAND}
                        value={filters.iotBrand}
                        onSelect={(option) => filters.setIotBrand(option?.value ?? null)}
                    />
                ) : null}
            </div>

            {filters.binSizeUpgraded ? (
                <p className='text-brand-muted text-right text-sm font-medium'>
                    <FormattedMessage
                        id='platform.analytics.insights.insight-detail-filter-bar.bin-upgraded'
                        description='Notice when bin size is auto-upgraded because the selected date range is too long'
                        defaultMessage='Bin size was increased so this range stays within safe query limits.'
                    />
                </p>
            ) : null}
            {filters.rangeClamped ? (
                <p className='text-brand-muted text-right text-sm font-medium'>
                    <FormattedMessage
                        id='platform.analytics.insights.insight-detail-filter-bar.range-clamped'
                        description='Notice when the date range is shortened to fit a locked bin size'
                        defaultMessage='Date range was shortened to fit the required bin size for this insight.'
                    />
                </p>
            ) : null}
        </div>
    );
};
